import { Component, Input, OnInit } from '@angular/core';
import { NotificationsService } from 'angular2-notifications';
import { finalize, Subject, take, debounceTime, distinctUntilChanged } from 'rxjs';
import { MemberApiService } from 'src/app/core/services/api/member-api.service';
import { UserApiService } from 'src/app/core/services/api/user-api.service';
import { ErrorHandlingService } from 'src/app/core/services/error-handling.service';
import { UsersGroup } from 'src/app/shared/enums/users-group.enum';

@Component({
  selector: 'app-agent-third-parties',
  templateUrl: './agent-third-parties.component.html',
  styleUrls: ['./agent-third-parties.component.scss'],
})
export class AgentThirdPartiesComponent implements OnInit {
  @Input() agentId!: number;

  // Attached list
  attachedThirdParties: any[] = [];
  isLoading = false;
  totalItems = 0;
  currentPage = 1;

  // Attach dialog
  showAttachDialog = false;
  productAdminsList: any[] = [];
  selectedThirdPartyIds: number[] = [];
  isLoadingProductAdmins = false;
  isAttaching = false;
  tpSearch = '';
  private tpSearchSubject = new Subject<string>();

  // Commissions dialog
  showCommissionsDialog = false;
  commissionsThirdParty: any = null;
  commissions: any[] = [];
  isLoadingCommissions = false;
  commissionsTotalItems = 0;
  commissionsCurrentPage = 1;
  commissionsRowsPerPage = 25;

  // Detach
  isDetaching: { [id: number]: boolean } = {};

  // Third-party users dialog
  showUsersDialog = false;
  usersThirdParty: any = null;
  thirdPartyUsers: any[] = [];
  availableSubUsers: any[] = [];
  selectedSubUserId: number | null = null;
  isLoadingThirdPartyUsers = false;
  isLoadingAvailableSubUsers = false;
  isAttachingSubUser = false;
  detachingSubUserId: number | null = null;
  usersCurrentPage = 1;
  usersRowsPerPage = 10;
  usersTotalItems = 0;

  constructor(
    private memberApi: MemberApiService,
    private userApiService: UserApiService,
    private notificationService: NotificationsService,
    private errHandle: ErrorHandlingService
  ) {}

  ngOnInit(): void {
    this.loadAttached();
    this.tpSearchSubject
      .pipe(debounceTime(400), distinctUntilChanged())
      .subscribe(() => this.loadProductAdmins());
  }

  loadAttached(): void {
    if (!this.agentId) return;
    this.isLoading = true;
    this.memberApi
      .getAttachedThirdParties(this.agentId, {
        page: this.currentPage,
      })
      .pipe(take(1), finalize(() => (this.isLoading = false)))
      .subscribe({
        next: (res) => {
          this.attachedThirdParties = this.extractDataList(res);
          this.totalItems = res?.meta?.total || this.attachedThirdParties.length;
        },
        error: (err) => this.errHandle.errorHandling(err),
      });
  }

  detach(thirdPartyId: number): void {
    this.isDetaching[thirdPartyId] = true;
    this.memberApi
      .detachThirdParties(this.agentId, [thirdPartyId])
      .pipe(
        take(1),
        finalize(() => (this.isDetaching[thirdPartyId] = false))
      )
      .subscribe({
        next: (res) => {
          this.notificationService.success('Success', res?.message || 'Detached successfully');
          this.loadAttached();
        },
        error: (err) => this.errHandle.errorHandling(err),
      });
  }

  // ─── Attach Dialog ──────────────────────────────────────────

  openAttachDialog(): void {
    this.showAttachDialog = true;
    this.productAdminsList = [];
    this.selectedThirdPartyIds = [];
    this.tpSearch = '';
    this.loadProductAdmins();
  }

  onTpSearchInput(): void {
    this.tpSearchSubject.next(this.tpSearch);
  }

  loadProductAdmins(): void {
    this.isLoadingProductAdmins = true;
    this.memberApi
      .getProductAdmins(this.tpSearch)
      .pipe(finalize(() => (this.isLoadingProductAdmins = false)))
      .subscribe({
        next: (res) => {
          this.productAdminsList = res?.data || [];
        },
        error: (err) => this.errHandle.errorHandling(err),
      });
  }

  toggleSelection(id: number): void {
    const idx = this.selectedThirdPartyIds.indexOf(id);
    if (idx > -1) {
      this.selectedThirdPartyIds.splice(idx, 1);
    } else {
      this.selectedThirdPartyIds.push(id);
    }
  }

  isSelected(id: number): boolean {
    return this.selectedThirdPartyIds.includes(id);
  }

  attach(): void {
    if (!this.selectedThirdPartyIds.length) {
      this.notificationService.error('Error', 'Please select at least one product admin');
      return;
    }
    this.isAttaching = true;
    this.memberApi
      .attachThirdParties(this.agentId, this.selectedThirdPartyIds)
      .pipe(
        take(1),
        finalize(() => (this.isAttaching = false))
      )
      .subscribe({
        next: (res) => {
          this.notificationService.success('Success', res?.message || 'Attached successfully');
          this.showAttachDialog = false;
          this.loadAttached();
        },
        error: (err) => this.errHandle.errorHandling(err),
      });
  }

  // ─── Commissions Dialog ─────────────────────────────────────

  openCommissions(tp: any): void {
    this.commissionsThirdParty = tp;
    this.commissions = [];
    this.commissionsCurrentPage = 1;
    this.showCommissionsDialog = true;
    this.loadCommissions();
  }

  loadCommissions(): void {
    this.isLoadingCommissions = true;
    const params: Record<string, string | number> = {
      page: this.commissionsCurrentPage,
      per_page: this.commissionsRowsPerPage,
    };
    if (this.commissionsThirdParty?.id) {
      params['third_party_id'] = this.commissionsThirdParty.id;
    }
    this.memberApi
      .getThirdPartyCommissions(this.agentId, params)
      .pipe(finalize(() => (this.isLoadingCommissions = false)))
      .subscribe({
        next: (res) => {
          this.commissions = res?.data || [];
          this.commissionsTotalItems = res?.meta?.total ?? this.commissions.length;
        },
        error: (err) => this.errHandle.errorHandling(err),
      });
  }

  onCommissionsPageChange(page: number): void {
    this.commissionsCurrentPage = page;
    this.loadCommissions();
  }

  // ─── Third-Party Users Dialog ────────────────────────────────

  openUsers(tp: any): void {
    this.usersThirdParty = tp;
    this.thirdPartyUsers = [];
    this.availableSubUsers = [];
    this.selectedSubUserId = null;
    this.usersCurrentPage = 1;
    this.usersTotalItems = 0;
    this.showUsersDialog = true;
    this.loadThirdPartyUsers();
    this.loadAvailableSubUsers();
  }

  loadThirdPartyUsers(): void {
    if (!this.agentId || !this.usersThirdParty?.id) return;

    this.isLoadingThirdPartyUsers = true;
    this.memberApi
      .getThirdPartyUsers(this.agentId, this.usersThirdParty.id, {
        length: this.usersRowsPerPage,
        page: this.usersCurrentPage,
      })
      .pipe(finalize(() => (this.isLoadingThirdPartyUsers = false)))
      .subscribe({
        next: (res) => {
          this.thirdPartyUsers = this.extractDataList(res);
          this.usersTotalItems = res?.meta?.total || this.thirdPartyUsers.length;
        },
        error: (err) => this.errHandle.errorHandling(err),
      });
  }

  loadAvailableSubUsers(): void {
    if (!this.agentId) return;

    this.isLoadingAvailableSubUsers = true;
    this.userApiService
      .getAll(UsersGroup.Agents, {
        filter_by_parent: this.agentId,
        length: 100,
      })
      .pipe(finalize(() => (this.isLoadingAvailableSubUsers = false)))
      .subscribe({
        next: (res) => {
          this.availableSubUsers = (res?.data || []).map((user: any) => ({
            ...user,
            displayName: this.getUserName(user),
          }));
        },
        error: (err) => this.errHandle.errorHandling(err),
      });
  }

  attachSubUser(): void {
    if (!this.selectedSubUserId || !this.usersThirdParty?.id) {
      this.notificationService.error('Error', 'Please select a sub-user');
      return;
    }

    this.isAttachingSubUser = true;
    this.memberApi
      .attachSubUserToThirdParty(this.selectedSubUserId, this.usersThirdParty.id)
      .pipe(
        take(1),
        finalize(() => (this.isAttachingSubUser = false))
      )
      .subscribe({
        next: (res) => {
          this.notificationService.success('Success', res?.message || 'Sub-user attached successfully');
          this.selectedSubUserId = null;
          this.loadThirdPartyUsers();
        },
        error: (err) => this.errHandle.errorHandling(err),
      });
  }

  detachSubUser(user: any): void {
    if (!user?.id || !this.usersThirdParty?.id) return;

    this.detachingSubUserId = user.id;
    this.memberApi
      .detachSubUserFromThirdParty(user.id, this.usersThirdParty.id)
      .pipe(
        take(1),
        finalize(() => (this.detachingSubUserId = null))
      )
      .subscribe({
        next: (res) => {
          this.notificationService.success('Success', res?.message || 'Sub-user detached successfully');
          this.loadThirdPartyUsers();
        },
        error: (err) => this.errHandle.errorHandling(err),
      });
  }

  onUsersPageChange(page: number): void {
    this.usersCurrentPage = page;
    this.loadThirdPartyUsers();
  }

  getUserName(user: any): string {
    const fullName = `${user?.first_name || ''} ${user?.last_name || ''}`.trim();
    return fullName || user?.name || user?.email || '-';
  }

  private extractDataList(res: any): any[] {
    if (Array.isArray(res?.data)) {
      return res.data;
    }

    if (Array.isArray(res?.data?.data)) {
      return res.data.data;
    }

    return [];
  }
}
